'use client'

import TemplateCard from '@/components/DashboardV2/TemplateCard'
import { useSessionAwareOnboardingQuery } from '@/lib/features/user/userApi'
import { AnimatePresence, motion } from 'framer-motion'

interface Template {
  readonly id: string
  readonly title: string
  readonly description: string
  readonly templateUrl: string
  readonly category: string
  readonly updatedAt: string
  readonly images: readonly string[]
}

type AutomationPreference =
  | 'seo'
  | 'ads'
  | 'research'
  | 'content-marketing'
  | 'influencer-automation'

// Default template suggestions for new users
const DEFAULT_TEMPLATES: readonly Template[] = [
  {
    id: 'website-content-analyzer',
    title: 'Website Content Analyzer',
    description: 'Analyze your website content and get SEO recommendations',
    templateUrl: 'https://www.sprites.ai/template/845d48de-1b7c-4578-b88d-fb18a70203e2',
    category: 'SEO',
    updatedAt: new Date().toISOString(),
    images: [],
  },
  {
    id: 'marketing-campaign-builder',
    title: 'Marketing Campaign Builder',
    description: 'Create and manage your marketing campaigns across platforms',
    templateUrl: 'https://www.sprites.ai/template/b7fd110a-3483-402b-9355-e42e6d8e03be:2',
    category: 'Marketing',
    updatedAt: new Date().toISOString(),
    images: [],
  },
  {
    id: 'meta-ads-competitor-researcher',
    title: 'Meta Ads Competitor Researcher',
    description:
      'Research competitor ad strategies on Meta and uncover winning creatives and hooks',
    templateUrl: 'https://www.sprites.ai/template/b2f6081b-de6b-5394-aced-f0b4736c3ef6',
    category: 'Advertising',
    updatedAt: new Date().toISOString(),
    images: [],
  },
]

// Personalized templates based on onboarding choices
const PERSONALIZED_TEMPLATES: Record<AutomationPreference, readonly Template[]> = {
  seo: [
    {
      id: 'seo-keyword-finder',
      title: 'SEO keyword finder',
      description: 'Find high-value keywords for your content strategy',
      templateUrl: 'https://www.sprites.ai/template/f77f7f2d-12b6-440a-aac5-b17ab4262d13',
      category: 'SEO',
      updatedAt: new Date().toISOString(),
      images: [],
    },
    {
      id: 'technical-seo-analyzer',
      title: 'Technical SEO analyzer',
      description: 'Analyze technical SEO issues on your website',
      templateUrl: 'https://www.sprites.ai/template/a90ad69c-a6b7-421c-bf5f-ec8a9ffb0ebe',
      category: 'SEO',
      updatedAt: new Date().toISOString(),
      images: [],
    },
  ],
  ads: [
    {
      id: 'google-ads-search-campaign-creator',
      title: 'Google Ads Search Campaign Creator',
      description:
        'Build high-converting search campaigns from scratch with keywords, ad copy, and targeting',
      templateUrl: 'https://www.sprites.ai/template/f3da550b-8afb-599f-a33a-4aa02cc5c1c4',
      category: 'Advertising',
      updatedAt: new Date().toISOString(),
      images: [],
    },
    {
      id: 'meta-ads-competitor-researcher',
      title: 'Meta Ads Competitor Researcher',
      description:
        'Research competitor ad strategies on Meta and uncover winning creatives and hooks',
      templateUrl: 'https://www.sprites.ai/template/b2f6081b-de6b-5394-aced-f0b4736c3ef6',
      category: 'Advertising',
      updatedAt: new Date().toISOString(),
      images: [],
    },
    {
      id: 'google-ads-campaign-optimizer',
      title: 'Google Ads Campaign Optimizer',
      description:
        'Analyze campaign performance and get actionable recommendations to optimize keywords and bids',
      templateUrl: 'https://www.sprites.ai/template/d4229fe0-6984-50a0-8e00-31e8c703a83b',
      category: 'Advertising',
      updatedAt: new Date().toISOString(),
      images: [],
    },
  ],
  research: [
    {
      id: 'app-store-review-scraper',
      title: 'App Store Review Scraper',
      description: 'Extract and analyze app store reviews for insights',
      templateUrl: 'https://www.sprites.ai/template/94cfa837-1644-497c-9e2d-7f8a4f827d47',
      category: 'Research',
      updatedAt: new Date().toISOString(),
      images: [],
    },
    {
      id: 'industry-trend-reddit-scanner',
      title: 'Industry Trend Reddit Scanner',
      description: 'Monitor Reddit for industry trends and discussions',
      templateUrl: 'https://www.sprites.ai/template/c5c9aa82-bfb7-423f-8281-03cee47b06c8',
      category: 'Research',
      updatedAt: new Date().toISOString(),
      images: [],
    },
    {
      id: 'content-gap-finder',
      title: 'Content Gap Finder',
      description: 'Identify content opportunities in your niche',
      templateUrl: 'https://www.sprites.ai/template/1d38627c-542e-4ecc-bfd4-b7b915a857fa',
      category: 'Research',
      updatedAt: new Date().toISOString(),
      images: [],
    },
  ],
  'content-marketing': [
    {
      id: 'b2b-linkedin-post-generator',
      title: 'B2B LinkedIn Post Generator',
      description: 'Create engaging LinkedIn posts for B2B audiences',
      templateUrl: 'https://www.sprites.ai/template/ffd5f766-dcdd-4e4c-8d3b-43b07d8930fe',
      category: 'Content Marketing',
      updatedAt: new Date().toISOString(),
      images: [],
    },
    {
      id: 'website-content-analyzer',
      title: 'Website content analyzer',
      description: 'Analyze your website content and get SEO recommendations',
      templateUrl: 'https://www.sprites.ai/template/845d48de-1b7c-4578-b88d-fb18a70203e2',
      category: 'Content Marketing',
      updatedAt: new Date().toISOString(),
      images: [],
    },
    {
      id: 'marketing-campaign-builder',
      title: 'Marketing Campaign Builder',
      description: 'Create and manage your marketing campaigns across platforms',
      templateUrl: 'https://www.sprites.ai/template/b7fd110a-3483-402b-9355-e42e6d8e03be:2',
      category: 'Content Marketing',
      updatedAt: new Date().toISOString(),
      images: [],
    },
  ],
  'influencer-automation': [
    {
      id: 'influencer-finder',
      title: 'Influencer Finder',
      description: 'Discover relevant influencers for your brand',
      templateUrl: 'https://www.sprites.ai/template/d6e39861-ae30-4e8a-9eee-3acaa7c49b5d',
      category: 'Influencer Marketing',
      updatedAt: new Date().toISOString(),
      images: [],
    },
    {
      id: 'outreach-campaign-builder',
      title: 'Outreach Campaign Builder',
      description: 'Build and manage influencer outreach campaigns',
      templateUrl: 'https://www.sprites.ai/template/8e579ed7-787a-48ec-82a0-18ca423585d4',
      category: 'Influencer Marketing',
      updatedAt: new Date().toISOString(),
      images: [],
    },
    {
      id: 'influencer-performance-analyzer',
      title: 'Influencer Performance Analyzer',
      description: 'Analyze influencer campaign performance and ROI',
      templateUrl: 'https://www.sprites.ai/template/e1a7bcad-38c7-4c3b-8de8-4cdc5c2d101a',
      category: 'Influencer Marketing',
      updatedAt: new Date().toISOString(),
      images: [],
    },
  ],
}

interface EmptyStateProps {
  hasSessions: boolean
  hasWorkflows: boolean
}

export default function EmptyState({ hasSessions, hasWorkflows }: EmptyStateProps) {
  const { data: onboardingData } = useSessionAwareOnboardingQuery()

  // Only show empty state if user has no sessions AND no workflows
  if (hasSessions || hasWorkflows) {
    return null
  }

  // Get personalized templates based on user's onboarding choices
  const getPersonalizedTemplates = (): readonly Template[] => {
    if (
      !onboardingData?.automationPreferences ||
      onboardingData.automationPreferences.length === 0
    ) {
      return DEFAULT_TEMPLATES
    }

    // Collect templates from all selected preferences
    const selectedTemplates: Template[] = []
    const addedTemplateIds = new Set<string>()

    onboardingData.automationPreferences.forEach((preference) => {
      if (preference in PERSONALIZED_TEMPLATES) {
        const templates = PERSONALIZED_TEMPLATES[preference as AutomationPreference]
        templates.forEach((template) => {
          if (!addedTemplateIds.has(template.id)) {
            selectedTemplates.push(template)
            addedTemplateIds.add(template.id)
          }
        })
      }
    })

    // If no personalized templates found, return default
    return selectedTemplates.length > 0 ? selectedTemplates : DEFAULT_TEMPLATES
  }

  const templatesToShow: readonly Template[] = getPersonalizedTemplates()

  return (
    <div className="pt-12">
      <div className="container mx-auto flex flex-col gap-y-4 px-3 pb-20 md:px-10">
        {/* Header */}
        <div className="mb-2 mt-2 flex w-full flex-col items-start gap-2 md:flex-row md:items-center md:justify-between">
          <motion.p
            className="text-left text-xl text-gray-900 md:text-2xl"
            style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '0.01em' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            Getting Started
          </motion.p>
        </div>

        {/* Template Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <ul className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            <AnimatePresence>
              {templatesToShow.map((template, index) => (
                <motion.li
                  key={template.id}
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.2, ease: 'easeInOut', delay: index * 0.1 }}
                >
                  <TemplateCard
                    id={template.id}
                    title={template.title}
                    description={template.description}
                    updatedAt={template.updatedAt}
                    images={template.images}
                    href={
                      template.templateUrl.startsWith('http')
                        ? template.templateUrl
                        : `/${template.templateUrl}`
                    }
                  />
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
        </motion.div>

        {/* Help Section */}
        <motion.div
          className="mt-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="text-center">
              <h3
                className="mb-3 text-xl font-semibold text-gray-900"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                Need help getting started?
              </h3>
              <p className="font-inter mb-6 text-gray-600">
                Get in touch with Sprites and let us guide you through the setup process.
              </p>

              <div className="flex flex-col justify-center gap-4 sm:flex-row">
                {/* Email Button */}
                <a
                  href="mailto:siamak@spritesai.com"
                  className="font-inter flex items-center justify-center gap-3 rounded-xl bg-gray-50 px-6 py-3 font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-100"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  Ask a question
                </a>

                {/* YouTube Button */}
                <a
                  href="https://www.youtube.com/@SiamakFromSprites"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-inter flex items-center justify-center gap-3 rounded-xl bg-gray-50 px-6 py-3 font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-100"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                  Watch Training Video
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
